import { JACHIN_REPORTING_API_URL } from "@solomon-apps/config";
import { fetchJson } from "@solomon-apps/shared";
import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

export interface ReportingEnvelope<T> {
    status: "available" | "stale" | "unavailable";
    fetchedAt: string | null;
    sourceAt: string | null;
    data: T | null;
}
export interface ReportingToken {
    totalSupplyUi: string;
    enrolledInEarnUi: string | null;
    enrolledAsOf: string | null;
    sourceApyPct: string | null;
}
export interface ReportingHolding {
    mint: string;
    symbol: string;
    vaultAddress: string | null;
    amount: string;
    amountUsd: string | null;
}
export interface ReportingBacking {
    asOf: string | null;
    totalUsd: string | null;
    valuation: string;
    holdings: ReportingHolding[];
}
export interface ReportingPayout {
    uuid: string;
    paidAt: string | null;
    amountUi: string;
    apyPct: string | null;
    txSignature: string | null;
    windowStartsAt: string;
    windowEndsAt: string;
}
export interface ReportingPayouts {
    lastPayoutAt: string | null;
    totalPaidUi: string;
    runCount: number;
    sourceApyPct: string | null;
    payouts: ReportingPayout[];
}
export interface ReportingHistoryRow {
    at: string;
    totalSupplyUi: string | null;
    totalUsd: string | null;
    holdings: Array<{ symbol: string; amount: string; amountUsd: string | null }>;
}
const prefix = `${JACHIN_REPORTING_API_URL}/api/solomon-protocol`;
function useReporting<T>(path: string): UseQueryResult<ReportingEnvelope<T>, Error> {
    return useQuery({
        queryKey: ["jachin-reporting", path],
        queryFn: async ({ signal }) => {
            const result = await fetchJson<ReportingEnvelope<T>>(`${prefix}/${path}`, signal);
            if (!result || !["available", "stale", "unavailable"].includes(result.status))
                throw new Error("Invalid reporting response");
            return result;
        },
        staleTime: 60_000,
        refetchInterval: 60_000,
        retry: 2
    });
}
export const useReportingToken = (): UseQueryResult<ReportingEnvelope<ReportingToken>, Error> =>
    useReporting<ReportingToken>("token-stats");
export const useReportingBacking = (): UseQueryResult<ReportingEnvelope<ReportingBacking>, Error> =>
    useReporting<ReportingBacking>("token-backing");
export const useReportingPayouts = (): UseQueryResult<ReportingEnvelope<ReportingPayouts>, Error> =>
    useReporting<ReportingPayouts>("payout-stats");
export async function fetchReportingHistory(
    path: "token-history" | "token-backing-history",
    from: string,
    to: string,
    signal?: AbortSignal
): Promise<ReportingHistoryRow[]> {
    const rows: ReportingHistoryRow[] = [];
    for (let offset = 0; offset < 100_000; offset += 1000) {
        const query = new URLSearchParams({ from, to, limit: "1000", offset: String(offset) });
        const page = await fetchJson<{ data: ReportingHistoryRow[]; hasMore: boolean }>(
            `${prefix}/${path}?${query}`,
            signal
        );
        if (!page || !Array.isArray(page.data) || typeof page.hasMore !== "boolean")
            throw new Error("Invalid reporting history");
        rows.push(...page.data);
        if (!page.hasMore) return rows;
        if (page.data.length !== 1000) throw new Error("Incomplete reporting history");
    }
    throw new Error("Reporting history exceeds limit");
}
export function useReportingHistory(
    path: "token-history" | "token-backing-history"
): UseQueryResult<ReportingHistoryRow[], Error> {
    const today = new Date().toISOString().slice(0, 10);
    const from = new Date(Date.parse(today) - 90 * 86400_000).toISOString();
    const to = new Date(Date.parse(today) + 86400_000).toISOString();
    return useQuery({
        queryKey: ["jachin-reporting", path, from, to],
        queryFn: ({ signal }) => fetchReportingHistory(path, from, to, signal),
        staleTime: 300_000,
        refetchInterval: 300_000
    });
}
